import { DragSource } from 'react-dnd';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import DoneIcon from '@material-ui/icons/Done';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import React from 'react';
import DeleteAdornment from '../../core/DeleteAdornment';
import Errored from '../../core/Errored';
import LanguageAdornment from '../../core/LanguageAdornment';
import AsteriskIcon from '../../core/icons/Asterisk';
import ListResult from '../../core/ListResult';
import { globalStyles } from '../../core/style/gstheme';
import { MAX_LITERAL_LENGTH } from '../../core/utils/constants';
import { createLengthWarning, getGraphFromProject, makeCancelable, sanitiseText, sortByLength } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';

const styles = Object.assign({}, globalStyles);

const Types = {
    ANNOTATION: 'annotation'
};

const annotationSource = {
    beginDrag(props) {
        return {
            annotationLabel: props.text,
            annotationLang: props.lang,
            type: "Annotation"
        };
    }
};

function collectSource(connect) {
    return {
        connectDragSource: connect.dragSource()
    };
}

class AnnotationField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            originalText: this.props.text,
            text: this.props.text,
            isEdited: false,
            acOpen: false,
            needsValidating: false,
            isDuplicate: false,
            existingValues: [],
            isErrored: null
        };
    }

    componentDidMount() {
        this.updateState(this.props.text);
    }

    componentDidUpdate() {
        if (this.state.needsValidating && this.state.text && this.state.text !== "") {
            this.setState({ needsValidating: false });
            this.checkExistingValues(this.state.text);
        }
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    static getDerivedStateFromProps(props, state) {
        if (state.text !== "" && props.existingValues && props.existingValues.length > 0) {
            let hasChanged = true;
            if (props.existingValues.length === state.existingValues.length) {
                hasChanged = props.existingValues.some(ev => !state.existingValues.some(sv => sv === ev));
            }
            else hasChanged = true;
            if (hasChanged) {
                return {
                    needsValidating: true,
                    existingValues: props.existingValues,
                    ...(props.text !== state.originalText) && {
                        text: props.text,
                        originalText: props.text,
                        acOpen: false,
                        isEdited: false,
                        isDuplicate: false
                    }
                };
            }
        }
        if (props.text !== state.originalText) {
            return {
                text: props.text,
                originalText: props.text,
                acOpen: false,
                isEdited: false,
                isDuplicate: false
            };
        }
        return null;
    }

    updateState = (text) => this.setState({ text: text ? text : "" });

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    findLabel = (value) => {
        const { lang, conceptIRI, xlLabelType } = this.props;
        let searchParts = [
            {
                predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                value: this.props.schemeIRI,
            },
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: [
                    "http://www.w3.org/2004/02/skos/core#Concept",
                    "http://www.w3.org/2008/05/skos-xl#Label"
                ]
            },
            {
                predicate: [
                    "http://www.w3.org/2004/02/skos/core#prefLabel",
                    "http://www.w3.org/2004/02/skos/core#altLabel",
                    "http://www.w3.org/2004/02/skos/core#hiddenLabel",
                    "http://www.w3.org/2008/05/skos-xl#literalForm"
                ],
                value: value,
                isExactMatch: false
            }
        ];
        this.doAutocomplete(
            {
                graph: [getGraphFromProject(this.props.projectIRI)],
                search: searchParts,
                language: lang,
                predicates: [
                    "http://www.w3.org/2004/02/skos/core#prefLabel",
                    "http://www.w3.org/2004/02/skos/core#altLabel",
                    "http://www.w3.org/2004/02/skos/core#hiddenLabel",
                    "https://grafsync.graphifi.com/schema/gs/iri",
                    "http://www.w3.org/2008/05/skos-xl#literalForm",
                    "https://graphifi.com/schema/xkos/isXlAltLabelOf",
                    "https://graphifi.com/schema/xkos/isXlPrefLabelOf",
                    "https://graphifi.com/schema/xkos/isXlHiddenLabelOf"
                ]
            }
        )
            .promise
            .then((data) => {
                const mappedResults = data.results && data.results.length > 0 ?
                    data.results
                        .map(result => {
                            const altLabels = result.altLabel && result.altLabel[lang] ?
                                Array.isArray(result.altLabel[lang]) ?
                                    result.altLabel[lang]
                                    :
                                    [result.altLabel[lang]]
                                :
                                [];
                            const hiddenLabels = result.hiddenLabel && result.hiddenLabel[lang] ?
                                Array.isArray(result.hiddenLabel[lang]) ?
                                    result.hiddenLabel[lang]
                                    :
                                    [result.hiddenLabel[lang]]
                                :
                                [];
                            return {
                                id: result.id,
                                iri: result.iri,
                                labelValues: [...altLabels, ...hiddenLabels],
                                label: result.prefLabel ?
                                    result.prefLabel
                                    :
                                    result.literalForm,
                                xlLabelOf: result.isXlPrefLabelOf || result.isXlAltLabelOf || result.isXlHiddenLabelOf || null,
                                xlLabelType: result.isXlPrefLabelOf ?
                                    "preferred"
                                    : result.isXlAltLabelOf ?
                                        "alternative"
                                        : result.isXlHiddenLabelOf ?
                                            "hidden"
                                            :
                                            null,
                                isXL: result.literalForm ? true : false
                            };
                        })
                    :
                    null;
                const sortedResults = mappedResults ? sortByLength([...mappedResults], 'label', lang).slice(0, 5) : null;
                const isDuplicate = mappedResults ?
                    mappedResults.some(result =>
                        // It's an XL label that matches another XL preferred label on the same concept
                        (result.isXL
                            && conceptIRI && result.xlLabelOf === conceptIRI
                            && result.label && result.label[lang] !== this.state.originalText
                            && result.label[lang].toLowerCase() === this.state.text.toLowerCase().trim())
                        // It's an XL alt or hidden label that matches on the same concept
                        || (result.isXL
                            && conceptIRI && result.xlLabelOf === conceptIRI
                            && result.labelValues.some(lv =>
                                lv !== this.state.originalText
                                && lv.toLowerCase() === this.state.text.toLowerCase().trim()))
                        // It's an alt or hidden label on the same concept
                        || (!result.isXL
                            && result.id === conceptIRI
                            && result.labelValues.some(lv =>
                                lv !== this.state.originalText
                                && lv.toLowerCase() === this.state.text.toLowerCase().trim()))
                        // It's another preferred label that matches if XL label is used as a preferred label
                        || (!(result.isXL && !result.xlLabelOf)
                            && (!xlLabelType || xlLabelType === "preferred")
                            && result.label && result.label[lang] !== this.state.originalText
                            && result.label[lang].toLowerCase() === this.state.text.toLowerCase().trim()))
                    :
                    false;
                this.setState({
                    results: sortedResults,
                    acOpen: mappedResults ? true : false,
                    isDuplicate: isDuplicate,
                    checking: false
                });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    checkExistingValues(text) {
        // If it's a case variation of original that is fine
        if (text.toLowerCase() === this.props.text.toLowerCase()) return true;
        if (this.props.existingValues && this.props.existingValues
            .map(value => value.toLowerCase())
            .includes(text.toLowerCase().trim())) {
            this.setState({ isDuplicate: true, acOpen: false, needsValidating: false });
            return false;
        }
        return true;
    }

    handleChange = (event) => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const cleanText = sanitiseText(t.value);
        this.setState({ text: cleanText, checking: true, isEdited: true });
        this.hcTimeout = setTimeout(() => {
            if (t.value.length > 0) {
                if (this.checkExistingValues(t.value) && this.props.checkValues) {
                    this.findLabel(t.value);
                }
                else {
                    this.setState({ checking: false });
                }
            }
            else {
                this.handleClickAway();
                this.setState({ checking: false });
            }
        }, 250);
    };

    handleUpdate = () => {
        this.setState({ isEdited: false, isDuplicate: true });
        this.props.onUpdate(this.state.text.trim());
    };

    handleClickAway = () => this.setState({ acOpen: false });

    reset = () => {
        this.setState({
            text: this.props.text.trim(),
            originalText: this.props.text,
            acOpen: false,
            isEdited: false,
            isDuplicate: false
        });
    };

    render() {

        const { connectDragSource, classes, isReadOnly, lang, property, onDelete, draggable, conceptIRI,
            defaultLang, canDeleteIfDefault, multiline, dataTest } = this.props;
        const { text, originalText, acOpen, isDuplicate, checking, isErrored, results, isEdited } = this.state;
        if (isErrored) return <Errored data={isErrored} />;

        let icon = null;
        if (checking) {
            icon =
                <Tooltip title="Waiting for server to check text">
                    <InputAdornment position="end">
                        <span style={{ marginRight: '5px' }}>...</span>
                    </InputAdornment>
                </Tooltip>;
        }
        else if (text.length > 0 && !isReadOnly && !isDuplicate && text !== originalText) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Confirm change" onClick={this.handleUpdate}>
                        <IconButton className={classes.smallIconButton}>
                            <DoneIcon data-test="done" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }
        else if (text === "" || (isDuplicate && text !== originalText)) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Text is duplicating an existing label">
                        <WarningIcon data-test="label-warning" className={classes.inputIcon} style={{ marginRight: '5px' }} />
                    </Tooltip>
                </InputAdornment>;
        }
        else if (text === originalText && !isReadOnly && (canDeleteIfDefault || lang !== defaultLang)) {
            icon = <DeleteAdornment dataTest="label" fontSize="small" onClick={onDelete} />;
        }
        else {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="The default language value cannot be deleted">
                        <AsteriskIcon data-test="no-delete" fontSize="small" className={classes.inputIcon} style={{ marginRight: '5px' }} />
                    </Tooltip>
                </InputAdornment>;
        }

        const annotationField = <div>
            <ClickAwayListener onClickAway={this.reset}>
                <TextField
                    data-test={dataTest}
                    name={property}
                    value={text}
                    fullWidth={true}
                    variant="outlined"
                    margin="dense"
                    multiline={multiline}
                    style={{ marginTop: 0 }}
                    onChange={this.handleChange}
                    error={text.trim().length === 0 || (isDuplicate && text !== originalText)}
                    placeholder="You must enter text"
                    helperText={
                        (isDuplicate && text !== originalText) ?
                            <span data-test="helper-text">This value already exists.</span>
                            :
                            createLengthWarning(text, MAX_LITERAL_LENGTH, isEdited)}
                    onKeyPress={(ev) => {
                        if (ev.charCode === 13) {
                            if (text !== originalText) {
                                if (!isDuplicate && !checking && text.trim().length > 0) {
                                    this.handleUpdate();
                                }
                            }
                            ev.preventDefault();
                            ev.stopPropagation();
                        }
                    }}
                    InputProps={
                        {
                            className: classes.inputLabel,
                            inputProps: {
                                "data-test": dataTest + "-input",
                                readOnly: isReadOnly,
                                maxLength: MAX_LITERAL_LENGTH
                            },
                            startAdornment: (<LanguageAdornment lang={lang} />),
                            endAdornment: icon
                        }
                    } />
            </ClickAwayListener>
            {acOpen &&
                <ClickAwayListener onClickAway={this.handleClickAway}>
                    <List
                        data-test="list-results"
                        dense
                        className={classes.existingResults}
                        subheader={<ListSubheader className={classes.existingResultsHeader}>Existing Labels</ListSubheader>}
                        style={{ marginTop: -2 }}>
                        {results && results.map((result, i) => {
                            let labelText = result.labelValues.filter(lv => lv.toLowerCase().includes(this.state.text.toLowerCase())).join(" AND ");
                            let label = result.label ?
                                result.label[lang]
                                :
                                "";
                            if (labelText) label += " (" + labelText + ")";
                            let additionalLabel = result.isXL && !result.xlLabelType ?
                                "XL label"
                                :
                                "Concept label";
                            if (conceptIRI && result.id === conceptIRI) {
                                additionalLabel += " on the same concept";
                            }
                            else if (conceptIRI && result.xlLabelOf === conceptIRI) {
                                additionalLabel += " on the same concept (XL " + result.xlLabelType + ")";
                            }
                            const currentIRI = result.iri ?
                                result.iri[result.iri.length - 1]
                                :
                                result.id;

                            return <ListResult
                                key={i}
                                text={label}
                                iri={currentIRI}
                                pendingDeletion={result.pendingDeletion}
                                invalid={true}
                                additionalLabel={additionalLabel}
                            />;
                        })}

                    </List>
                </ClickAwayListener>
            }
        </div>;
        return (
            isReadOnly || !draggable ? annotationField : connectDragSource(annotationField)
        );
    }
}


AnnotationField.defaultProps = {
    isReadOnly: false,
    multiline: false,
    draggable: false
};

AnnotationField.propTypes = {
    draggable: PropTypes.bool,
    dataTest: PropTypes.string,
    defaultLang: PropTypes.string,
    text: PropTypes.string,
    isReadOnly: PropTypes.bool,
    checkValues: PropTypes.bool,
    conceptIRI: PropTypes.string,
    xlLabelType: PropTypes.string,
    schemeIRI: PropTypes.string,
    lang: PropTypes.string.isRequired,
    property: PropTypes.string.isRequired,
    existingValues: PropTypes.array,
    projectIRI: PropTypes.string.isRequired,
    multiline: PropTypes.bool,
    canDeleteIfDefault: PropTypes.bool.isRequired,
    onDelete: PropTypes.func,
    onUpdate: PropTypes.func.isRequired,
    classes: PropTypes.object
};

const af = DragSource(Types.ANNOTATION, annotationSource, collectSource)(withStyles(styles)(AnnotationField));

export default af;